import { alpha, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { MutableRefObject, RefObject } from 'react';

import { VoiceCaptionSpeakerIcon } from 'assets/svg';
import type { AppDispatch } from 'features/app/store/store';
import { useCaptionPruning } from 'features/call/call-captions/hooks';
import type {
  CaptionLineForTurns,
  CaptionSpeakerColorVariant,
  CaptionTurn,
} from 'features/call/call-captions/helpers';
import styled from 'features/theme/styled';

const CAPTION_LINE_HEIGHT_DEFAULT = 1.65;
const CAPTION_LINE_HEIGHT_COMPACT = 1.6;
const CAPTION_LINE_HEIGHT_TIGHT = 1.55;
const CAPTION_LINE_HEIGHT_MINIMUM = 1.5;

interface VoiceMeetingCaptionsProps {
  turns: CaptionTurn[];
  captionLinesRef: RefObject<HTMLDivElement>;
  captionTextRefs: MutableRefObject<Record<string, HTMLElement | null>>;
  dispatch: AppDispatch;
  visibleCaptions: CaptionLineForTurns[];
  trimOffsets: Record<string, number>;
  captionsEnabled: boolean;
  resizeTick: number;
  onPrunedToEmpty?: () => void;
}

export const VoiceMeetingCaptions = ({
  turns,
  captionLinesRef,
  captionTextRefs,
  dispatch,
  visibleCaptions,
  trimOffsets,
  captionsEnabled,
  resizeTick,
  onPrunedToEmpty,
}: VoiceMeetingCaptionsProps) => {
  useCaptionPruning({
    dispatch,
    visibleCaptions,
    renderedTurns: turns,
    trimOffsets,
    containerRef: captionLinesRef,
    captionTextRefs,
    captionsEnabled,
    resizeTick,
    onPrunedToEmpty,
  });

  return (
    <Container data-testid='voice-meeting-captions'>
      <CaptionViewport data-testid='voice-meeting-captions-viewport'>
        <CaptionShell>
          <CaptionLines
            ref={captionLinesRef}
            data-testid='voice-meeting-captions-lines'
          >
            {turns.map((caption) => (
              <CaptionEntry key={caption.key}>
                <CaptionSpeakerLine>
                  <CaptionSpeakerIcon
                    $colorVariant={caption.speakerColorVariant}
                    aria-hidden='true'
                    data-testid='voice-meeting-captions-speaker-icon'
                  />
                  <CaptionSpeaker variant='body1'>
                    {caption.speakerLabel}
                  </CaptionSpeaker>
                </CaptionSpeakerLine>
                <CaptionText
                  ref={(element) => {
                    if (!captionTextRefs.current) {
                      captionTextRefs.current = {};
                    }

                    captionTextRefs.current[caption.key] = element;
                  }}
                  variant='body1'
                >
                  {caption.text}
                </CaptionText>
              </CaptionEntry>
            ))}
          </CaptionLines>
        </CaptionShell>
      </CaptionViewport>
    </Container>
  );
};

const Container = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  pointer-events: none;
`;

const CaptionViewport = styled('div')`
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-sizing: border-box;
  border-radius: 8px;
  background-color: ${({ theme }) => alpha(theme.palette.common.black, 0.84)};
`;

const CaptionShell = styled('div')`
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: stretch;
  background-color: transparent;
  color: ${({ theme }) => theme.palette.common.white};
`;

const CaptionLines = styled('div')`
  margin: 0;
  width: 55%;
  max-width: 55%;
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  font-size: 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;
  overflow-wrap: anywhere;
  user-select: none;
  -webkit-user-select: none;
  pointer-events: none;

  @media (max-width: 1399px) {
    width: 75%;
    max-width: 75%;
  }

  @media (max-width: 999px) {
    width: 85%;
    max-width: 85%;
  }

  @media (max-height: 999px) {
    padding: ${({ theme }) => theme.spacing(1, 1.5)};
    gap: ${({ theme }) => theme.spacing(0.75)};
  }

  @media (max-height: 799px) {
    padding: ${({ theme }) => theme.spacing(0.75, 1)};
    gap: ${({ theme }) => theme.spacing(0.5)};
  }

  @media (max-height: 699px) {
    padding: ${({ theme }) => theme.spacing(0.5, 1)};
  }
`;

const CaptionEntry = styled('div')`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing(1)};

  @media (max-height: 999px) {
    gap: ${({ theme }) => theme.spacing(0.75)};
  }

  @media (max-height: 799px) {
    gap: ${({ theme }) => theme.spacing(0.5)};
  }

  @media (max-height: 699px) {
    gap: ${({ theme }) => theme.spacing(0.25)};
  }
`;

const CaptionSpeakerLine = styled('div')`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-height: 999px) {
    gap: ${({ theme }) => theme.spacing(3)};
  }

  @media (max-height: 799px) {
    gap: ${({ theme }) => theme.spacing(2)};
  }

  @media (max-height: 699px) {
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

const getCaptionSpeakerIconFill = (
  theme: Theme,
  colorVariant: CaptionSpeakerColorVariant
) => {
  switch (colorVariant) {
    case 'local':
      return alpha(theme.palette.grey[800], 0.95);
    case 'primaryHearing':
      return alpha(theme.palette.primary[900], 0.85);
    case 'additionalHearing1':
      return alpha(theme.palette.tangerine[700], 0.9);
    case 'additionalHearing2':
      return alpha(theme.palette.wavello.active, 0.9);
    case 'unknown':
    default:
      return alpha(theme.palette.primary[900], 0.85);
  }
};

const CaptionSpeakerIcon = styled(VoiceCaptionSpeakerIcon)<{
  $colorVariant: CaptionSpeakerColorVariant;
}>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  @media (max-height: 999px) {
    width: 16px;
    height: 16px;
  }

  @media (max-height: 799px) {
    width: 14px;
    height: 14px;
  }

  @media (max-height: 699px) {
    width: 12px;
    height: 12px;
  }

  & .caption-speaker-bg {
    fill: ${({ theme, $colorVariant }) =>
      getCaptionSpeakerIconFill(theme, $colorVariant)};
  }

  & .caption-speaker-stroke {
    stroke: ${({ theme }) => theme.palette.grey[400]};
  }
`;

const CaptionSpeaker = styled(Typography)`
  color: ${({ theme }) => theme.palette.grey[400]};
  font-weight: 600;
  font-size: 20px;

  @media (max-height: 999px) {
    font-size: 16px;
  }

  @media (max-height: 799px) {
    font-size: 14px;
  }

  @media (max-height: 699px) {
    font-size: 12px;
  }
`;

const CaptionText = styled(Typography)`
  padding-left: ${({ theme }) => theme.spacing(13)};
  overflow-wrap: anywhere;
  line-height: ${CAPTION_LINE_HEIGHT_DEFAULT};
  font-size: 22px;

  @media (max-height: 999px) {
    padding-left: ${({ theme }) => theme.spacing(10)};
    line-height: ${CAPTION_LINE_HEIGHT_COMPACT};
    font-size: 18px;
  }

  @media (max-height: 799px) {
    padding-left: ${({ theme }) => theme.spacing(8)};
    line-height: ${CAPTION_LINE_HEIGHT_TIGHT};
    font-size: 16px;
  }

  @media (max-height: 699px) {
    padding-left: ${({ theme }) => theme.spacing(6)};
    line-height: ${CAPTION_LINE_HEIGHT_MINIMUM};
    font-size: 14px;
  }
`;
